import { useState } from 'react';
import { LayoutGrid, Map, ChevronLeft, ChevronRight } from 'lucide-react';
import { SiteCard } from './SiteCard';
import { AustraliaMap } from './AustraliaMap';
import type { Site } from '../types';

interface SitesSectionProps {
  sites: Site[];
  sitesPerPage?: number;
}

type ViewMode = 'cards' | 'map';

export function SitesSection({ sites, sitesPerPage = 6 }: SitesSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(sites.length / sitesPerPage);
  const startIndex = currentPage * sitesPerPage;
  const visibleSites = sites.slice(startIndex, startIndex + sitesPerPage);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
  };

  return (
    <section className="mt-8">
      {/* Section Header with Tabs */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Sites ({sites.length})
        </h2>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('cards')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'cards'
                ? 'bg-card dark:bg-brand-navy text-brand-orange shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            Cards
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'map'
                ? 'bg-card dark:bg-brand-navy text-brand-orange shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Map className="w-4 h-4" />
            Map
          </button>
        </div>
      </div>

      {/* Content */}
      {sites.length > 0 ? (
        <>
          {viewMode === 'cards' ? (
            <>
              {/* Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleSites.map((site) => (
                  <SiteCard key={site.id} site={site} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => goToPage(i)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          i === currentPage
                            ? 'bg-ink text-on-ink'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Page Info */}
              {totalPages > 1 && (
                <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
                  Showing {startIndex + 1}-{Math.min(startIndex + sitesPerPage, sites.length)} of {sites.length} sites
                </p>
              )}
            </>
          ) : (
            /* Map View */
            <AustraliaMap sites={sites} />
          )}
        </>
      ) : (
        <div className="card p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No sites configured yet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Click "Seed Demo Data" to create sample sites
          </p>
        </div>
      )}
    </section>
  );
}
